import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";
const WEBFORM_ENDPOINT = "https://api.dev.auxo.dk/webform/submit";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Honeypot check: if filled, reject silently or show error
    if (formData.website_url) {
      console.warn('Spam detected via honeypot');
      setStatus('success'); // Pretend success to bots
      return;
    }

    try {
      const response = await fetch(WEBFORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }

      setStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        website_url: ''
      });
    } catch (error) {
      console.error('Webform submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Der opstod en netværksfejl. Kontroller din forbindelse og prøv igen.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt | Klinik Acu-sense</title>
        <meta name="description" content="Kontakt Klinik Acu-sense i Hammel. Bestil tid online, ring eller send en mail. Find adresse og rutevejledning her." />
        <meta property="og:title" content="Kontakt | Klinik Acu-sense" />
        <meta property="og:description" content="Kontakt Klinik Acu-sense i Hammel. Bestil tid online, ring eller send en mail. Find adresse og rutevejledning her." />
        <link rel="canonical" href="https://www.acu-sense.dk/kontakt" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-[#F3F1EC] py-16 border-b border-[#E6E4DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C3531] tracking-tight">
            Kontakt mig
          </h1>
          <p className="text-base sm:text-lg text-[#5C6360] max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål til mine behandlinger, eller ønsker du at bestille en tid? Jeg er altid klar til at hjælpe dig videre.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Contact Info & Map */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
                  Klinik Acu-sense
                </h2>
                <p className="text-sm sm:text-base text-[#5C6360] leading-relaxed">
                  Klinikken er beliggende i rolige og professionelle omgivelser i Hammel. Der er gode parkeringsmuligheder samt fuld adgang for gangbesværede og kørestolsbrugere.
                </p>
              </div>

              {/* Info Cards */}
              <div className="space-y-4">
                {/* Flytteinformation */}
                <div className="p-5 rounded-2xl bg-[#5F7464]/10 border border-[#5F7464]/20 flex items-start gap-3.5">
                  <MapPin className="w-5 h-5 text-[#5F7464] shrink-0 mt-0.5" />
                  <div className="text-sm text-[#2C3531]">
                    <span className="font-semibold block text-[#3D4F44] mb-0.5">Vigtig information om flytning:</span>
                    Fra 1. september 2026 flytter jeg klinikken til anden adresse: <strong className="font-semibold text-[#2C3531]">Banealle 8F, 8450 Hammel</strong>.
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAF9F6] border border-[#E6E4DF]">
                  <MapPin className="w-6 h-6 text-[#5F7464] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-base font-bold text-[#2C3531] mb-1">Adresse</h3>
                    <p className="text-sm text-[#5C6360] leading-relaxed">
                      Banealle 8F<br />
                      8450 Hammel
                    </p>
                    <a
                      href="https://maps.google.com/maps?&daddr=Banealle+8F,+8450+Hammel"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-xs font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200 mt-2"
                    >
                      Få rutevejledning &rarr;
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAF9F6] border border-[#E6E4DF]">
                  <Phone className="w-6 h-6 text-[#5F7464] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-base font-bold text-[#2C3531] mb-1">Telefon</h3>
                    <p className="text-sm text-[#5C6360] leading-relaxed whitespace-nowrap">
                      2045 6563
                    </p>
                    <p className="text-xs text-[#8A908D] mt-1">
                      Læg gerne en besked eller send en sms, hvis jeg er optaget af behandling, så kontakter jeg dig hurtigst muligt.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#FAF9F6] border border-[#E6E4DF]">
                  <Mail className="w-6 h-6 text-[#5F7464] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-base font-bold text-[#2C3531] mb-1">E-mail</h3>
                    <p className="text-sm text-[#5C6360] leading-relaxed whitespace-nowrap">
                      info@acu-sense.dk
                    </p>
                  </div>
                </div>
              </div>

              {/* Extra Info */}
              <div className="text-xs text-[#8A908D] space-y-1">
                <p>CVR: 40203850</p>
                <p>Der er gode parkeringsforhold og adgang for gangbesværede og kørestole.</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-[#FAF9F6] rounded-3xl border border-[#E6E4DF] p-6 sm:p-10 shadow-sm space-y-8">
                <div className="space-y-2">
                  <h2 className="font-serif text-2xl font-bold text-[#2C3531]">
                    Skriv direkte til mig
                  </h2>
                  <p className="text-sm text-[#5C6360]">
                    Udfyld formularen nedenfor, og jeg vil vende tilbage til dig så hurtigt som muligt.
                  </p>
                </div>

                {status === 'success' ? (
                  <div className="p-6 rounded-2xl bg-[#8FA89B]/10 border border-[#8FA89B]/30 space-y-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-[#8FA89B]/20 flex items-center justify-center mx-auto text-[#5F7464]">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-serif text-lg font-bold text-[#2C3531]">Beskeden er sendt!</h3>
                      <p className="text-sm text-[#5C6360] max-w-md mx-auto">
                        Mange tak for din henvendelse. Jeg har modtaget din besked og vil vende tilbage til dig hurtigst muligt.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                    <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }}>
                      <label htmlFor="website_url">Website URL (skal efterlades tom)</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1.5">
                      <label htmlFor="name" className="text-xs font-bold text-[#2C3531] uppercase tracking-wider">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-[#D1CFC9] text-sm text-[#2C3531] focus:outline-none focus:border-[#5F7464] focus:ring-1 focus:ring-[#5F7464] transition-all duration-200"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Phone */}
                      <div className="space-y-1.5">
                        <label htmlFor="phone" className="text-xs font-bold text-[#2C3531] uppercase tracking-wider">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white border border-[#D1CFC9] text-sm text-[#2C3531] focus:outline-none focus:border-[#5F7464] focus:ring-1 focus:ring-[#5F7464] transition-all duration-200"
                          placeholder="F.eks. 2045 6563"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-1.5">
                        <label htmlFor="email" className="text-xs font-bold text-[#2C3531] uppercase tracking-wider">
                          E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white border border-[#D1CFC9] text-sm text-[#2C3531] focus:outline-none focus:border-[#5F7464] focus:ring-1 focus:ring-[#5F7464] transition-all duration-200"
                          placeholder="F.eks. navn@mail.dk"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-1.5">
                      <label htmlFor="message" className="text-xs font-bold text-[#2C3531] uppercase tracking-wider">
                        Beskrivelse <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-[#D1CFC9] text-sm text-[#2C3531] focus:outline-none focus:border-[#5F7464] focus:ring-1 focus:ring-[#5F7464] transition-all duration-200 resize-none"
                        placeholder="Skriv din besked eller beskriv dine symptomer her..."
                      />
                    </div>

                    {/* Error message */}
                    {status === 'error' && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs flex items-start gap-2.5">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-[#5F7464] text-white text-sm font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {status === 'submitting' ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send besked
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-20 bg-[#FAF9F6] border-t border-[#E6E4DF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
            Vil du hellere bestille en tid med det samme?
          </h2>
          <p className="text-sm sm:text-base text-[#5C6360] max-w-xl mx-auto">
            Gå direkte til online booking og find en ledig tid, der passer dig. Det tager under 2 minutter.
          </p>
          <div className="pt-4">
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-md"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Gå til booking
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
